import { Box } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import useColors from "../../../../../../api/hooks/useColors";
import StyledDataGrid from "../../../../../../components/StyledDataGrid";
import { isComplexEntityType } from "../../../../../../utils/utils";
import EntityDrawer from "../../../../../stack/environment/components/EntityDrawer/EntityDrawer";
import { useEntities } from "../../../../../stack/environment/hooks/useEntities";
import EntityTypeTabs from "./EntityTypeTabs";
import { getTabbedTableSX } from "./controlEntitiesTableUtils";

/*
  entitiesByType structure should look like:
  {
    "host": {
      [{}, {}, ...]
    },
    ...  
  }
  */
export default function EntityTypeTabbedTable({
  entitiesByType,
  columns,
  selectedEntityTypeTab,
  setSelectedEntityTypeTab,
  handleCheckedChange,
}) {
  const colors = useColors();
  const [searchParams, setSearchParams] = useSearchParams();
  const { flattenEntity, flattenEntities, useQueryForEntitiesByType } =
    useEntities();

  const onRowClick = (row) => {
    if (isComplexEntityType(row.entity_type)) {
      const params = new URLSearchParams(searchParams);
      params.set("entity", encodeURIComponent(row.entity_id));
      setSearchParams(params.toString());
    }
  };

  const drawerEntity = entitiesByType[selectedEntityTypeTab]?.find(
    (entity) =>
      entity.entity_id === decodeURIComponent(searchParams.get("entity"))
  );

  const entitiesQuery = useQueryForEntitiesByType({
    type: selectedEntityTypeTab,
    enabled: Boolean(selectedEntityTypeTab && drawerEntity),
  });

  const cachedDrawerEntity = entitiesQuery.data?.find(
    (e) => e.entity_id === drawerEntity?.entity_id
  );

  const rows = flattenEntities(entitiesByType[selectedEntityTypeTab]);

  return (
    <Box
      sx={{
        borderRadius: "10px",
        boxShadow: "0px 0px 2px 1px rgba(203, 203, 203, 0.50);",
        backgroundColor: colors.common_white,
      }}
    >
      <Box
        sx={{
          border: "none",
          borderRadius: "10px 10px 0 0",
          overflow: "hidden",
        }}
      >
        <EntityTypeTabs
          entitiesByTabs={entitiesByType}
          selectedEntityTypeTab={selectedEntityTypeTab}
          setSelectedEntityTypeTab={setSelectedEntityTypeTab}
        />
      </Box>
      <StyledDataGrid
        boxSXProps={{
          minHeight: "300px",
          border: "none",
          borderRadius: "0 0 10px 10px",
          ".MuiDataGrid-root": {
            border: "none",
            boxShadow: "none",
          },
          ".MuiDataGrid-main": {
            minHeight: "300px",
          },
        }}
        getRowId={(row) => row.entity_id}
        checkboxSelection
        disableColumnMenu
        sx={getTabbedTableSX(colors)}
        columns={columns}
        rows={rows}
        onCellClick={(params, event) => {
          if (!["__check__"].includes(params.field)) {
            event.stopPropagation();
          }
          if (
            ![
              "__check__",
              "add_to_asset",
              "affected_entity_ids",
              "extra_info_column",
              "actions",
            ].includes(params.field)
          ) {
            onRowClick(params.row);
          }
        }}
        onRowSelectionModelChange={handleCheckedChange}
        pageSizeOptionsOverride={[5, 10, 20, 50]}
        initialState={{
          sorting: {
            sortModel: [{ field: "affected_entity_ids", sort: "desc" }],
          },
        }}
      />
      <EntityDrawer
        openDrawer={drawerEntity}
        selectedEntity={flattenEntity(cachedDrawerEntity || drawerEntity)}
        onClose={() => {
          const params = new URLSearchParams(searchParams);
          params.delete("entity");
          setSearchParams(params.toString());
        }}
      />
    </Box>
  );
}
